import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ManageExamsProps {
  domain: string;
}

interface Exam {
  id: string;
  examId: string;
  examName: string;
  examType: string;
  academicYear: string;
  class: string;
  section: string;
  subject: string;
  teacherId: string;
  teacherName: string;
  examDate: string;
  startTime: string;
  endTime: string;
  duration: number;
  totalMarks: number;
  passingMarks: number;
  syllabusTopics: string[];
  status: string;
  createdAt: string;
}

const ManageExams: React.FC<ManageExamsProps> = ({ domain }) => {
  const [activeView, setActiveView] = useState<'list' | 'create'>('list');
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    examName: '',
    examType: 'unit-test',
    academicYear: '2025-26',
    class: '',
    section: '',
    subject: '',
    teacherId: '',
    teacherName: '',
    examDate: '',
    startTime: '',
    endTime: '',
    totalMarks: '',
    passingMarks: '',
    syllabusTopics: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const examTypes = [
    { value: 'unit-test', label: 'Unit Test' },
    { value: 'mid-term', label: 'Mid Term' },
    { value: 'final', label: 'Final Examination' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'half-yearly', label: 'Half Yearly' },
    { value: 'annual', label: 'Annual' },
  ];

  useEffect(() => {
    fetchExams();
  }, [domain]);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, `school-data/${domain}/exams`),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const examList: Exam[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Exam));
      setExams(examList);
    } catch (error: any) {
      console.error('Error fetching exams:', error);
      alert(`Failed to load exams: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const calculateDuration = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    return (endHour * 60 + endMin) - (startHour * 60 + startMin);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(false);

    try {
      const duration = calculateDuration(formData.startTime, formData.endTime);
      const syllabusArray = formData.syllabusTopics
        .split(',')
        .map(topic => topic.trim())
        .filter(topic => topic.length > 0);

      const examData = {
        examId: `exam_${Date.now()}`,
        examName: formData.examName,
        examType: formData.examType,
        academicYear: formData.academicYear,
        class: formData.class,
        section: formData.section,
        subject: formData.subject,
        teacherId: formData.teacherId,
        teacherName: formData.teacherName,
        examDate: formData.examDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        duration,
        totalMarks: Number(formData.totalMarks),
        passingMarks: Number(formData.passingMarks),
        syllabusTopics: syllabusArray,
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await addDoc(collection(db, `school-data/${domain}/exams`), examData);

      setSuccess(true);
      // Reset form
      setFormData({
        examName: '',
        examType: 'unit-test',
        academicYear: '2025-26',
        class: '',
        section: '',
        subject: '',
        teacherId: '',
        teacherName: '',
        examDate: '',
        startTime: '',
        endTime: '',
        totalMarks: '',
        passingMarks: '',
        syllabusTopics: '',
      });

      // Refresh list
      setTimeout(() => {
        fetchExams();
        setActiveView('list');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating exam:', error);
      alert(`Failed to create exam: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (examId: string) => {
    if (!confirm('Are you sure you want to delete this exam?')) return;

    try {
      await deleteDoc(doc(db, `school-data/${domain}/exams`, examId));
      alert('Exam deleted successfully!');
      fetchExams();
    } catch (error: any) {
      console.error('Error deleting exam:', error);
      alert(`Failed to delete exam: ${error.message}`);
    }
  };

  const handleStatusChange = async (examId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, `school-data/${domain}/exams`, examId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      alert('Status updated successfully!');
      fetchExams();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const getFilteredExams = () => {
    return exams.filter(exam => {
      if (filterClass && exam.class !== filterClass) return false;
      if (filterSection && exam.section !== filterSection) return false;
      if (filterStatus !== 'all' && exam.status !== filterStatus) return false;
      if (filterType !== 'all' && exam.examType !== filterType) return false;
      return true;
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: string } = {
      scheduled: 'bg-primary',
      ongoing: 'bg-warning',
      completed: 'bg-success',
      cancelled: 'bg-danger',
    };
    return badges[status] || 'bg-secondary';
  };

  const getExamTypeLabel = (type: string) => {
    return examTypes.find(et => et.value === type)?.label || type;
  };

  const filteredExams = getFilteredExams();

  if (activeView === 'create') {
    return (
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-calendar-plus me-2 text-primary-custom"></i>
              Schedule Exam
            </h5>
            <small className="text-muted">Create and schedule examinations</small>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setActiveView('list')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to List
          </button>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Exam Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.examName}
                  onChange={(e) => handleInputChange('examName', e.target.value)}
                  placeholder="e.g., Mid Term Examination 2025"
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Exam Type *</label>
                <select
                  className="form-select"
                  value={formData.examType}
                  onChange={(e) => handleInputChange('examType', e.target.value)}
                  required
                >
                  {examTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Academic Year *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.academicYear}
                  onChange={(e) => handleInputChange('academicYear', e.target.value)}
                  placeholder="e.g., 2025-26"
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Class *</label>
                <select
                  className="form-select"
                  value={formData.class}
                  onChange={(e) => handleInputChange('class', e.target.value)}
                  required
                >
                  <option value="">Select Class</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Class {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Section *</label>
                <select
                  className="form-select"
                  value={formData.section}
                  onChange={(e) => handleInputChange('section', e.target.value)}
                  required
                >
                  <option value="">Select Section</option>
                  {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                    <option key={sec} value={sec}>
                      Section {sec}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Subject *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="e.g., Mathematics"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Exam Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.examDate}
                  onChange={(e) => handleInputChange('examDate', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Start Time *</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">End Time *</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.endTime}
                  onChange={(e) => handleInputChange('endTime', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Duration (minutes)</label>
                <input
                  type="text"
                  className="form-control"
                  value={calculateDuration(formData.startTime, formData.endTime)}
                  disabled
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Teacher ID *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.teacherId}
                  onChange={(e) => handleInputChange('teacherId', e.target.value)}
                  placeholder="e.g., 17PVSS0001"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Teacher Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.teacherName}
                  onChange={(e) => handleInputChange('teacherName', e.target.value)}
                  placeholder="e.g., John Doe"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Total Marks *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.totalMarks}
                  onChange={(e) => handleInputChange('totalMarks', e.target.value)}
                  min="1"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Passing Marks *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.passingMarks}
                  onChange={(e) => handleInputChange('passingMarks', e.target.value)}
                  min="1"
                  required
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Syllabus Topics (comma-separated)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.syllabusTopics}
                  onChange={(e) => handleInputChange('syllabusTopics', e.target.value)}
                  placeholder="e.g., Chapter 1, Chapter 2, Chapter 3"
                />
                <small className="text-muted">Separate topics with commas</small>
              </div>
            </div>

            {success && (
              <div className="alert alert-success mt-4">
                <i className="bi bi-check-circle me-2"></i>
                Exam scheduled successfully!
              </div>
            )}

            <div className="mt-4">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Scheduling...
                  </>
                ) : (
                  <>
                    <i className="bi bi-calendar-check me-2"></i>
                    Schedule Exam
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-clipboard-check me-2 text-primary-custom"></i>
              Manage Exams
            </h5>
            <small className="text-muted">Schedule and manage examinations</small>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setActiveView('create')}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Schedule New Exam
          </button>
        </div>

        {/* Filters */}
        <div className="row g-2 mt-3">
          <div className="col-md-2">
            <select
              className="form-select form-select-sm"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  Class {i + 1}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select form-select-sm"
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
            >
              <option value="">All Sections</option>
              {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                <option key={sec} value={sec}>
                  Section {sec}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select form-select-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Exam Types</option>
              {examTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="col-md-2 text-end">
            <button
              className="btn btn-outline-secondary btn-sm w-100"
              onClick={() => {
                setFilterClass('');
                setFilterSection('');
                setFilterStatus('all');
                setFilterType('all');
              }}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading exams...</p>
          </div>
        ) : filteredExams.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-calendar-x display-1 text-muted"></i>
            <p className="mt-3 text-muted">
              {exams.length === 0
                ? 'No exams scheduled yet. Click "Schedule New Exam" to get started.'
                : 'No exams match the selected filters.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Exam Name</th>
                  <th>Type</th>
                  <th>Class/Section</th>
                  <th>Subject</th>
                  <th>Date & Time</th>
                  <th>Duration</th>
                  <th>Marks</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExams.map((exam) => (
                  <tr key={exam.id}>
                    <td>
                      <strong>{exam.examName}</strong>
                      <br />
                      <small className="text-muted">{exam.academicYear}</small>
                    </td>
                    <td>
                      <span className="badge bg-secondary">
                        {getExamTypeLabel(exam.examType)}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">
                        {exam.class} - {exam.section}
                      </span>
                    </td>
                    <td>{exam.subject}</td>
                    <td>
                      {exam.examDate}
                      <br />
                      <small className="text-muted">
                        {exam.startTime} - {exam.endTime}
                      </small>
                    </td>
                    <td>{exam.duration} min</td>
                    <td>
                      {exam.totalMarks}
                      <br />
                      <small className="text-muted">Pass: {exam.passingMarks}</small>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(exam.status)}`}>
                        {exam.status}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                          title="Change Status"
                        >
                          <i className="bi bi-arrow-repeat"></i>
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <a
                              className="dropdown-item"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleStatusChange(exam.id, 'scheduled');
                              }}
                            >
                              Scheduled
                            </a>
                          </li>
                          <li>
                            <a
                              className="dropdown-item"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleStatusChange(exam.id, 'ongoing');
                              }}
                            >
                              Ongoing
                            </a>
                          </li>
                          <li>
                            <a
                              className="dropdown-item"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleStatusChange(exam.id, 'completed');
                              }}
                            >
                              Completed
                            </a>
                          </li>
                          <li>
                            <a
                              className="dropdown-item text-danger"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleStatusChange(exam.id, 'cancelled');
                              }}
                            >
                              Cancelled
                            </a>
                          </li>
                        </ul>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete"
                          onClick={() => handleDelete(exam.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Stats */}
        {!loading && filteredExams.length > 0 && (
          <div className="row mt-4">
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body text-center">
                  <h3 className="mb-0">{filteredExams.length}</h3>
                  <small className="text-muted">Total Exams</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-primary bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-primary">
                    {filteredExams.filter(e => e.status === 'scheduled').length}
                  </h3>
                  <small className="text-muted">Scheduled</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-warning bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-warning">
                    {filteredExams.filter(e => e.status === 'ongoing').length}
                  </h3>
                  <small className="text-muted">Ongoing</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-success">
                    {filteredExams.filter(e => e.status === 'completed').length}
                  </h3>
                  <small className="text-muted">Completed</small>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageExams;
